import {useState} from 'react';
import {Card, Spinner} from 'react-bootstrap';
import {useIntl} from 'react-intl';
import Level3Title from '../../../../app/common/components/title/Level3Title';
import LoadingOverlay from '../../../../app/common/components/LoadingOverlay';
import {WlanBand, WlanInterface} from '../../Types/routerForms';
import {useGetWlan, useUpdateWifi} from '../../Hooks/RouterManager';
import EditWifiModal from './EditWifiModal';

interface WlanPageProps {
  routerId: string;
  band: WlanBand;
}

const WlanPage = ({routerId, band}: WlanPageProps): JSX.Element => {
  const intl = useIntl();
  const {data, isLoading, isFetching} = useGetWlan(routerId);
  const [editing, setEditing] = useState<WlanInterface | null>(null);
  const updateWifi = useUpdateWifi(routerId);

  // Onglet "2.4 GHz" → interfaces 2ghz ; onglet "5 GHz" → interfaces 5ghz et 6ghz.
  const interfaces = (data ?? []).filter((it) =>
    band === '2.4GHz' ? it.mode === '2ghz' : it.mode !== '2ghz'
  );

  const handleSave = (next: WlanInterface) =>
    updateWifi.mutate({
      id: next.id,
      ssid: next.ssid,
      password: next.password,
      active: next.active,
      mode: next.mode,
    });

  if (isLoading) {
    return (
      <div className="text-center py-10">
        <Spinner animation="border" />
      </div>
    );
  }

  return (
    <LoadingOverlay isLoading={isFetching}>
      <Card>
        <Card.Header>
          <Card.Title>
            <Level3Title
              title={intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.TITLE'}, {band})}
            />
          </Card.Title>
        </Card.Header>
        <Card.Body>
          <p className="text-muted mb-6">
            {intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.SUBTITLE'})}
          </p>
          <table className="table table-row-dashed align-middle">
            <thead>
              <tr className="fw-semibold text-muted">
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.INTERFACE'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.MODE'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.SSID'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.PASSWORD'})}</th>
                <th>{intl.formatMessage({id: 'CONFIGURATION.ROUTEUR.WLAN.STATUS'})}</th>
              </tr>
            </thead>
            <tbody>
              {interfaces.map((it) => (
                <tr key={it.id}>
                  <td>{it.name}</td>
                  <td>{it.mode.toUpperCase()}</td>
                  <td>{it.ssid}</td>
                  <td>
                    <span className="me-3">••••••••</span>
                    <button
                      type="button"
                      className="btn btn-link btn-sm p-0"
                      onClick={() => setEditing(it)}
                    >
                      {intl.formatMessage({id: 'COMMON.EDIT'})}
                    </button>
                  </td>
                  <td>
                    <span className={`badge bg-${it.active ? 'success' : 'secondary'}`}>
                      {intl.formatMessage({
                        id: it.active
                          ? 'CONFIGURATION.ROUTEUR.WLAN.STATUS_ON'
                          : 'CONFIGURATION.ROUTEUR.WLAN.STATUS_OFF',
                      })}
                    </span>
                    {it.about && <div className="text-muted small mt-1">{it.about}</div>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          <EditWifiModal
            show={editing !== null}
            onHide={() => setEditing(null)}
            initial={editing}
            band={band}
            onSave={handleSave}
          />
        </Card.Body>
      </Card>
    </LoadingOverlay>
  );
};

export default WlanPage;
